import React from 'react';
import { useDispatch } from 'react-redux';
import { useToastNotification } from '@/hooks';
import { closeModal, setModal, setDocumentStatus } from '@/state/actions';
import { shareEstimate } from '@/modules/dataWrangler/estimates';
import { SendIcon } from '@/resources/icons';
import { delayAwait } from '@/util';
import store from '@/state/store';
import { useSaveEstimate } from './useSaveEstimate';

/**
 * Estimate Hook
 * Sends an estimate, where data is passed into the hook.
 * Handles redirection logic if estimates limited, and
 * creating or updating functionality.
 *
 * Returns false, or clean estimate data.
 */
export const useSendEstimate = () => {
    const dispatch = useDispatch();
    const { triggerToast } = useToastNotification();
    const saveEstimate = useSaveEstimate();
    const subscriptionData =
        store.getState()?.user?.activeBusiness?.subscription || {};

    const { productId } = subscriptionData || undefined;

    const sendEstimate = async (
        estimateData,
        emailOverwrite,
        emailSubject,
        emailBody
    ) => {
        const startTime = Date.now();
        dispatch(setModal({ slug: 'loader-estimate-send' }));

        let savedData;

        if (estimateData?.documentState === 'draft' || productId === 'free') {
            savedData = await saveEstimate(estimateData, 'send');

            if (savedData === false) {
                dispatch(closeModal('loader-estimate-send'));
                return false;
            }
        } else {
            savedData = { ...estimateData };
        }

        const documentId = savedData?.documentId;

        if (documentId !== null) {
            savedData = await shareEstimate(
                documentId,
                'email',
                emailOverwrite,
                emailSubject,
                emailBody
            );

            if (savedData?.status === 429) {
                await delayAwait(startTime, 2000);

                dispatch(closeModal('loader-estimate-send'));

                triggerToast({
                    message: 'Please wait before sending again',
                    action: 'delete',
                    icon: <SendIcon />
                });

                return false;
            }

            // If draft, and API returned correctly - update invoice state.
            if (
                savedData?.documentState &&
                savedData?.documentState === 'draft'
            ) {
                dispatch(setDocumentStatus(savedData.documentState));
            }

            await delayAwait(startTime, 2000);

            dispatch(closeModal('loader-estimate-send'));

            triggerToast({
                message: 'Estimate Sent',
                action: 'send',
                icon: <SendIcon />
            });

            return savedData;
        }

        return false;
    };

    return sendEstimate;
};
